import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { debounce, map, sortBy, startsWith, includes, find, isEmpty, isNull, isUndefined } from "lodash";
import { Typography, Divider } from "@material-ui/core";
import { AutoComplete } from "components/inputs";
import { searchDrugs } from "app/main/applications/actions/applications.actions";
import { Alert } from "@material-ui/lab";
import isSearchStringValid from "helpers/is-search-string-valid";

const normaliseOptions = drugs => map(drugs, d => ({
  label: d.drugFullName,
  value: d,
}));

export default function DrugSelector({ required, isCreatable = true, initialDrugOptions, orgUnitId, onChange, ...other }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [options, setOptions] = useState(initialDrugOptions ? normaliseOptions(initialDrugOptions) : []);
  const [isOpen, setOpen] = useState(!!initialDrugOptions);
  const [hasSearched, setHasSearched] = useState(false); // used to prevent showing the "add to list" option on isCreatable inputs before the user has input a valid search

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onInputChange = useCallback(debounce(async (event, val, action) => {
    if (action !== "input" || !isSearchStringValid(val, 2)) {
      setHasSearched(false);
      return;
    }

    setLoading(true);

    const res = await dispatch(searchDrugs(orgUnitId, { searchString: val }));

    setOptions(res.error ? [] : normaliseOptions(res.payload));
    setApiError(res.error ? "Error loading medications" : null);
    setLoading(false);
    setHasSearched(true);
    if (initialDrugOptions) {
      setOpen(true);
    }
  }, 300), [dispatch, orgUnitId, initialDrugOptions]);

  const onSelectChange = value => {
    if (onChange) {
      onChange(value);
    }
    setOpen(false);
    setLoading(false);
  };

  const drugSorter = (allOptions, inputValue) => sortBy(allOptions, [option => !startsWith(option.value.drugFullName, inputValue), option => !includes(option.value.drugFullName, inputValue)]);

  const renderFormLabel = value => {
    const { onFormularies } = value;

    let ofFormulariesLabel = " (Not on any Formulary)";

    if (!isEmpty(onFormularies)) {
      ofFormulariesLabel = " (On Formularies: ";

      for (let i = 0; i < sortBy(onFormularies, ["id"]).length; i += 1) {
        if (i > 0) {
          ofFormulariesLabel += ", ";
        }

        ofFormulariesLabel += `${onFormularies[i].name}`;
      }

      ofFormulariesLabel += ")";
    }

    return (<Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>{`${ofFormulariesLabel}`}</Typography>);
  };

  // Hide label if onFormularies prop is null or undefined (backward compatibility)
  const showFormularyLabel = drugResult => !isNull(drugResult.onFormularies) && !isUndefined(drugResult.onFormularies);

  return (
    <>
      <AutoComplete
        isCreatable={isCreatable}
        loading={loading}
        options={options}
        hasSearched={hasSearched}
        onInputChange={onInputChange}
        onBlur={() => setOpen(false)}
        required={required}
        renderOption={({ label, value }) => {
          // If multiple results come back with the same form name, e.g., "injection" for morphine, then show the full name
          const singularFormNameInResults = isEmpty(find(options, x => x.value.formName === value?.formName // Check that the short name is the same
            && x.value.formId !== value?.formId // Check that they're different forms
            && x.value.drugId === value?.drugId, // Ensure it's only on the same drug
          // eslint-disable-next-line function-paren-newline
          ));
          // True => Show formName
          // False => show fullFormName

          return (value ? (
            <div className="flex flex-col flex-1">
              <Typography className="font-bold">{value.drugFullName}</Typography>
              <Typography variant="caption" color="textSecondary">{(singularFormNameInResults) ? value.formName : value.fullFormName}
                {showFormularyLabel(value) ? renderFormLabel(value) : null}
              </Typography>
              <Divider />
            </div>
          ) : label);
        }}
        onChange={onSelectChange}
        {...other}
        onOpen={() => setOpen(true)}
        open={isOpen}
        ignoreInputMatchingFilter
        onClose={() => setOptions([])}
        sorter={drugSorter}
        handleOnChange={onSelectChange}
      />
      {apiError && <Alert severity="warning">{apiError}</Alert>}
    </>
  );
}
